import { CommonModule, DatePipe, getLocaleDateFormat, JsonPipe, ɵnormalizeQueryParams } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, NgModule, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, NgModel, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule, MatDateRangeInput } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { IgxGridModule } from '@infragistics/igniteui-angular';
import { IgxGridComponent, IgxTabsModule } from '@infragistics/igniteui-angular';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { AuthService } from 'src/app/core/services/auth-security/auth.service';
import { MessageService } from 'src/app/core/services/broadcast/message-service';
import { DataService } from 'src/app/core/services/data/data.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { ParkerStateService } from 'src/app/core/services/state/parker-state.service';
import { IDateRangeForQuery, IPaymentReportQuery, IPaymentReportResult } from 'src/app/shared/interfaces';
import { HeaderTitleLotDataComponent } from 'src/app/shared/ui/header-title-lot-data/header-title-lot-data.component';
import { aceDates } from '../../helpers/transform/aceDates';
import { IgxFinancialChartDefaultTemplatesComponent } from 'igniteui-angular-charts';
import { YearSelectorDirectiveStandalone } from '../../directives/set-date-current-year-standalone.directive';

@Component({
  selector: 'app-date-selection',
  standalone: true,
  imports: [
    MatSlideToggleModule,
    MatExpansionModule,
    MatIconModule,
    CommonModule,
    MatTabsModule,
    MatSnackBarModule,
    MatTableModule,
    MatCardModule,
    MatOptionModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    IgxGridModule,
    IgxTabsModule,
    MatSelectModule,
    DatePipe,
    FormsModule,
    YearSelectorDirectiveStandalone,
  ],
  templateUrl: './date-selection.component.html',
  styleUrl: './date-selection.component.scss',
})
export class DateSelectionComponent implements OnInit {
  @Output() dateOutput = new EventEmitter<IDateRangeForQuery>();

  selected = 'today';
  fromDate: Date = new Date();
  toDate: Date = new Date();
  dateSelectControl = new FormControl(new Date());
  rangeErrorMessage: string = '';

  readonly rangeForm = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  //Services
  _appGeneralState = inject(AppGeneralStateService);

  constructor() {}

  ngOnInit(): void {
    this.rangeForm.valueChanges.subscribe(() => {
      this.onDateRangeSelectionChange();
    });

    this.dateSelectControl.valueChanges.subscribe((value) => {
      this.onDateSelectionChange(value);
    });

    this._appGeneralState.resetDateComponent.subscribe(() => {
      this.initDates();
    });
  }

  initDates(): void {
    this.selected = 'today';
    this.fromDate = new Date();
    this.toDate = new Date();
    this.rangeErrorMessage = '';
    this.publishDates();
  }

  onDateTypePickerSelectionChange(event: any): void {
    var dateTransform: aceDates = new aceDates();

    if (event.value == 'today') {
      this.fromDate = new Date();
      this.toDate = new Date();
    } else if (this.selected == 'yesterday') {
      this.onSetYesterday();
    } else if (event.value == '7days') {
      this.toDate = new Date();
      this.fromDate = dateTransform.dateAdd(new Date(), 'day', -7);
    } else if (event.value == 'mtd') {
      this.onSetMonthToDate();
    } else if (event.value == '30days') {
      this.toDate = new Date();
      this.fromDate = dateTransform.dateAdd(new Date(), 'day', -30);
    } else if (event.value == 'specificdate') {
      this.toDate = new Date();
      this.fromDate = new Date();
    } else if (event.value == 'dateRange') {
      this.toDate = new Date();
      this.fromDate = new Date();
    } else if (event.value == 'previousmonth') {
      this.onSetPreviousMonth();
    } else if (event.value == 'currentyear') {
      this.onSetCurrentYear();
    }

    this.publishDates();
  }

  onSetCurrentYear(): void {
    var dateTransform: aceDates = new aceDates();

    //Set the Start Date
    const date = new Date();
    const month: number = date.getMonth();
    const currentYear: number = date.getFullYear();
    this.fromDate = new Date(currentYear, 0, 1);

    //Set the End Date
    this.toDate = new Date();

    this.publishDates();
  }

  onSetMonthToDate(): void {
    var dateTransform: aceDates = new aceDates();

    this.toDate = new Date();
    const date = new Date();
    const month: number = date.getMonth();
    const currentYear: number = date.getFullYear();
    this.fromDate = new Date(currentYear, month, 1);

    this.publishDates();
  }

  onSetPreviousMonth(): void {
    var dateTransform: aceDates = new aceDates();

    this.toDate = new Date();

    //Set the Start Date
    this.fromDate = dateTransform.dateAdd(new Date(), 'month', -1);
    const fromMonth: number = this.fromDate.getMonth();
    const fromCurrentYear: number = this.fromDate.getFullYear();
    this.fromDate = new Date(fromCurrentYear, fromMonth, 1);

    //Set the EndDate
    const rightNow: Date = new Date();
    const toMonth: number = rightNow.getMonth();
    const toCurrentYear: number = new Date().getFullYear();
    const currentFirstDayOfMonth: Date = new Date(toCurrentYear, toMonth, 1);
    this.toDate = dateTransform.dateAdd(currentFirstDayOfMonth, 'day', -1);

    this.publishDates();
  }

  onSetYesterday(): void {
    var dateTransform: aceDates = new aceDates();

    this.fromDate = dateTransform.dateAdd(new Date(), 'day', -1);
    this.toDate = dateTransform.dateAdd(new Date(), 'day', -1);

    this.publishDates();
  }

  onDateRangeSelectionChange() {
    this.fromDate = this.rangeForm.get('start')?.value;
    this.toDate = this.rangeForm.get('end')?.value;

    this.rangeErrorMessage = '';

    if (this.fromDate == undefined || this.fromDate == null) {
      this.rangeErrorMessage = 'Please select the Start Date';
    }

    if (this.toDate == undefined || this.toDate == null) {
      this.rangeErrorMessage = 'Please select the End Date';
    }

    if (this.fromDate > this.toDate && this.rangeErrorMessage == '') {
      this.rangeErrorMessage = 'The Start Date cannot be after the End Date';
    }

    this.publishDates();
  }

  onDateSelectionChange(dateValue: Date) {
    this.fromDate = dateValue;
    this.toDate = dateValue;

    this.publishDates();
  }

  publishDates(): void {
    var dateRange: IDateRangeForQuery = {} as IDateRangeForQuery;

    dateRange.startDate = this.fromDate;
    dateRange.endDate = this.toDate;

    this.dateOutput.emit(dateRange);
  }
}
